"use client";

import clsx from "clsx";
import type { SettlementLeg } from "@/lib/sse/useSSE";
import { CdmState } from "@/components/cdm/CdmState";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 3.5 — Settlement Simulation. Perspective swaps back to Aurora.
 * Timeline fast-forwards (visible "16×" badge). Money moves through
 * Orbitra Global's settlement rails — anonymised CDM events for AED + BRL legs.
 * Three-field reconciliation: VAR ✓ E2EID ✓ InvoiceNumber ✓.
 * Invoice FSM hits MATCHED. HCS anchor confirms.
 *
 * Settlement vendors stay back-office — no WIO / Mercado Bitcoin / Zand
 * branding surfaces here.
 */
export function SettlementSimulation({
  legs,
  threeFieldMatched,
  invoiceMatched,
  className,
}: {
  legs: SettlementLeg[];
  threeFieldMatched: { var: boolean; e2eId: boolean; invoiceNumber: boolean } | null;
  invoiceMatched: boolean;
  className?: string;
}) {
  const aedLeg = legs.find((l) => l.leg === "AED");
  const brlLeg = legs.find((l) => l.leg === "BRL");
  const t = useTranslations("settlement");

  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("kicker")}</p>
            <h3 className="scene-h2 mt-1 text-orbitra-paper">{t("title")}</h3>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Leg label={t("buyerLegLabel")}  leg={aedLeg} />
          <Leg label={t("sellerLegLabel")} leg={brlLeg} />
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 text-[11px]">
          <ThreeField label="VAR"                    ok={threeFieldMatched?.var} />
          <ThreeField label="E2E ID"                 ok={threeFieldMatched?.e2eId} />
          <ThreeField label={t("invoiceNumberLabel")} ok={threeFieldMatched?.invoiceNumber} />
        </div>

        <div className="mt-auto pt-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("invoiceFsmLabel")}</p>
          <div className="mt-2 flex items-center gap-2">
            <CdmState token={invoiceMatched ? "MATCHED" : "SINGLE_WINDOW_SUBMITTED"} />
            {invoiceMatched && (
              <span className="rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-policy-pass">
                {t("threeFieldChip")}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("mercatorLabel")}</p>
          <p className="mt-2 text-orbitra-paper">
            "{t("mercatorQuote")}"
          </p>
        </div>
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("audienceSeesTitle")}</p>
          <ul className="mt-2 space-y-1 text-orbitra-teal-soft">
            <li>• <span className="font-mono">{t("eventSettlementReceived")}</span> {t("audienceSeesAedLeg")}</li>
            <li>• <span className="font-mono">{t("eventSettlementReceived")}</span> {t("audienceSeesBrlLeg")}</li>
            <li>• <span className="font-mono">{t("eventThreeFieldReconciled")}</span></li>
            <li>• <span className="font-mono">{t("eventInvoiceMatched")}</span></li>
          </ul>
          <p className="mt-2 text-[10px] text-orbitra-muted">
            {t("vendorsHiddenNote")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Leg({ label, leg }: { label: string; leg: SettlementLeg | undefined }) {
  const t = useTranslations("settlement");
  return (
    <div className={clsx(
      "rounded-md border px-3 py-3 text-[11px]",
      leg
        ? "border-state-financed/50 bg-state-financed/8 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
        : "border-orbitra-muted/30 bg-orbitra-ink/30 text-orbitra-muted",
    )}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">
        {leg ? t("legReceived") : t("legAwaiting")}
      </p>
      <p className="mt-1 font-display text-base font-semibold text-orbitra-paper">{label}</p>
      <p className="mt-1 text-[10px]">
        {leg ? <>{t("railPrefix")} <span className="font-mono">{leg.rail}</span></> : "—"}
      </p>
    </div>
  );
}

function ThreeField({ label, ok }: { label: string; ok?: boolean }) {
  const t = useTranslations("settlement");
  return (
    <div className={clsx(
      "flex items-center justify-between rounded-md border px-3 py-2",
      ok
        ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
        : "border-orbitra-muted/30 text-orbitra-muted",
    )}>
      <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
      <span className="text-[10px]">{ok ? t("matchedTag") : "·"}</span>
    </div>
  );
}
